<script lang="ts">
  import type { App } from '$lib/types';
  import type { Prompt } from '$routes/auth/account/_components/auth';

  import { getContext, createEventDispatcher, onMount } from 'svelte';
  import { BASE_APP_URL, STATIC_URL } from '$lib/constants';
  import { analytics, includeScript } from '$lib/utils';
  import { EOETracking } from '$lib/tracking';
  import { Toasts, conversionPageEmail } from '$lib/stores';
  import { fetch } from '$lib/utils';
  import { dayjs } from '@metafy/dates';
  import { page } from '$app/stores';

  import FormControl from '$components/FormControl.svelte';
  import OAuthButtons from './OAuthButtons.svelte';

  import IconSpeechTail from '$assets/svgs/speech_tail.svg?component';
  import SpaceManAnimation from './illustrations/SpaceManAnimation.svelte';
  import Button from '$mds/Buttons/Button';
  import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
  import { PUBLIC_METAFY_ENV } from '$env/static/public';

  const { irkAccount, refreshSession } = getContext<App>('app');
  const dispatch = createEventDispatcher();

  let className = '';
  export { className as class };
  export let variant: 'full' | 'contained' = 'full';
  export let prompt: Prompt | null = null;
  export let switchHref = false;

  let isCreatingAccount = false;
  let login = null;
  let password = null;
  let turnstileId: string;
  let turnstileToken: string;

  async function onCreate() {
    try {
      isCreatingAccount = true;
      const response = await fetch(`${BASE_APP_URL}/auth/proxy/create.json`, {
        method: 'post',
        body: JSON.stringify({
          name: '',
          login,
          password,
          turnstileToken,
          timezone: dayjs.tz.guess(),
          locale: window.navigator.language || 'en-US'
        })
      });
      if (!response) {
        throw new Error();
      }
      const json = await response.json();

      // Bad request
      if (response.status >= 400) {
        throw new Error(json.message);
      }

      // Great! We're in 🚀
      await refreshSession(json.accessToken, json.refreshToken);

      // Analytics
      analytics.track({
        name: 'Created Account',
        props: { method: 'metafy' },
        options: { context: { traits: { userId: $irkAccount!.id, email: $irkAccount!.email } } }
      });

      dispatch('needsUsername');
    } catch (error) {
      Toasts.push({
        kind: 'error',
        content: (error as Error).message ?? 'There was an issue creating your account. Please try again.'
      });
      // Reset the Turnstile widget
      if (turnstileId) {
        window.turnstile?.reset(turnstileId);
      }
    } finally {
      isCreatingAccount = false;
    }
  }

  /**
   * @returns {string | undefined} the validation error
   */
  function validate() {
    if (!login) {
      return 'You must fill in your email address.';
    }
    if (!password) {
      return 'You must fill in your password.';
    }
    if (password.length < 6) {
      return 'Your password must be longer than 6 characters.';
    }
  }

  function handleSubmit(e) {
    analytics.track({ name: EOETracking.stepOneTracking.TRACK_SIGNUP_CTA });
    const error = validate();
    if (error) {
      e.preventDefault();
      return Toasts.push({
        kind: 'error',
        content: error
      });
    }
  }

  onMount(() => {
    login = $conversionPageEmail;
    if (PUBLIC_METAFY_ENV === 'production') {
      includeScript('https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit')!.then(() => {
        turnstileId = window.turnstile?.render('#cf-turnstile', {
          sitekey: '0x4AAAAAAA2I2DDRmPKZhsJh',
          theme: 'dark',
          callback: function (token: string) {
            turnstileToken = token;
          }
        });
      });
    }
    return () => {
      if (turnstileId) {
        window.turnstile?.remove(turnstileId);
      }
    };
  });
</script>

<div class="{className} relative mx-auto flex w-full flex-col xl:my-auto xl:mt-auto xl:flex-row">
  {#if variant === 'full'}
    {#if prompt}
      <div
        class="bg-ondark-medium border-ondark-subtle absolute z-20 -mt-[100px] ml-[26px] hidden max-w-[340px] -rotate-[4deg] rounded-xl border p-4 xl:inline-block"
      >
        <div class="text-xl font-semibold text-white">{prompt.title}</div>
        <div class="text-md mt-2 font-medium">
          {prompt.description}
        </div>
        <div class="text-action-ondark-active mt-3 text-xs">
          This will only take a minute, <span class="text-white">so hold on to that thought.</span>
        </div>
        <IconSpeechTail class="absolute -bottom-[91px] left-[205px] h-[95px] rotate-[7deg]" />
      </div>
    {/if}
    <div
      class="illustration-bg relative rounded-3xl xl:w-1/2"
      style="--illustration-url: url('{STATIC_URL}/illustrations/astronaut_new_bg.svg');"
    >
      <SpaceManAnimation
        class="relative -left-[5%] m-auto max-h-40 w-[600px] max-w-64 md:max-h-none xl:-left-[10%] xl:-top-[10%] xl:max-h-[120%]  xl:max-w-none"
      />
    </div>
  {/if}
  <div class="{variant === 'full' ? 'xl:basis-1/2' : ''} px-6 xl:my-auto xl:w-1/2 xl:px-10 xl:pt-6">
    {#if variant === 'full'}
      <h3 class="text-display-sm text-ondark-primary mb-6 text-center xl:text-left">Sign up</h3>
    {/if}
    <OAuthButtons
      type="create"
      on:after={async () => {
        dispatch('needsUsername');
      }}
    />

    <hr class="border-ondark-subtle my-6" />

    <form on:submit|preventDefault={onCreate} class="flex flex-col">
      <div class="mb-6 flex flex-col">
        <div>
          <FormControl
            id="email"
            type="email"
            placeholder="Email address"
            value={$conversionPageEmail}
            on:input={event => (login = event.target.value)}
          />
        </div>

        <div class="mt-5">
          <FormControl
            id="password"
            type="password"
            inlinePadding={52}
            placeholder="Password"
            on:input={event => (password = event.target.value)}
          />
        </div>

        <div id="cf-turnstile"></div>

        <strong class="text-ondark-secondary mt-4 text-sm font-normal">
          By signing up, you agree with our
          <a
            on:click={() => analytics.track({ name: EOETracking.stepOneTracking.TRACK_TOS })}
            href="https://metafy.gg/legal/terms"
            class="text-ondark-primary hover:text-action-ondark-base transition-colors hover:underline"
            rel="noopener noreferrer external"
            target="_blank">ToS</a
          >,
          <a
            href="https://metafy.gg/legal/conduct"
            class="text-ondark-primary hover:text-action-ondark-base transition-colors hover:underline"
            rel="noopener noreferrer external"
            target="_blank"
          >
            Community Guidelines
          </a>
          and
          <a
            on:click={() => analytics.track({ name: EOETracking.stepOneTracking.TRACK_PRIVACY })}
            href="https://metafy.gg/legal/privacy"
            class="text-ondark-primary hover:text-action-ondark-base transition-colors hover:underline"
            rel="noopener noreferrer external"
            target="_blank"
          >
            Privacy Policy
          </a>
        </strong>
      </div>

      <Button
        size="lg"
        type="submit"
        loading={isCreatingAccount}
        on:click={handleSubmit}
        icon={faArrowRight}
        iconPosition="right"
      >
        Sign up
      </Button>

      <!-- Switch to "Create account" -->
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <svelte:element
        this={switchHref ? 'a' : 'button'}
        href={switchHref ? '/auth/account/login' + $page.url.search : null}
        role={switchHref ? 'link' : 'button'}
        on:click={() => {
          if (!switchHref) {
            dispatch('switch');
          }
        }}
        class="text-ondark-primary mt-5 text-left font-medium hover:underline"
      >
        Already have an account?
        <span class="text-action-ondark-base">Log in</span>
      </svelte:element>
    </form>
  </div>
</div>

<style>
  .illustration-bg {
    @screen xl {
      background-image: var(--illustration-url);
    }
  }
</style>
